import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus, BookOpen, Users, DollarSign, MoreVertical, 
  Edit, Trash2, Eye, EyeOff, Loader2, TrendingUp, Zap, ArrowRight, BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InstructorDashboard = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [coursesRes, statsRes, planRes, earningsRes] = await Promise.all([
        axios.get(`${API}/courses/instructor`, { headers }),
        axios.get(`${API}/instructor/stats`, { headers }),
        axios.get(`${API}/instructor/plan-status`, { headers }),
        axios.get(`${API}/instructor/earnings`, { headers })
      ]);
      setCourses(coursesRes.data);
      setStats(statsRes.data);
      setPlanStatus(planRes.data);
      setEarnings(earningsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePublish = async (courseId, currentStatus) => {
    try {
      await axios.put(`${API}/courses/${courseId}`, {
        is_published: !currentStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentStatus ? 'Course unpublished' : 'Course published!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update course');
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    try {
      await axios.delete(`${API}/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Course deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const handleCreateCourse = () => {
    if (planStatus?.subscription_status !== 'active') {
      toast.error('Please subscribe to a plan first');
      navigate('/instructor/pricing');
      return;
    }
    if (!planStatus?.can_create_more) {
      toast.error('Course limit reached. Please upgrade your plan.');
      navigate('/instructor/pricing');
      return;
    }
    navigate('/instructor/courses/new');
  };

  const isSubscribed = planStatus?.subscription_status === 'active';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="instructor-dashboard">
      <Navbar />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900">
                Instructor Dashboard
              </h1>
              <p className="text-slate-600 mt-1">Manage your courses and track earnings</p>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => navigate('/instructor/analytics')}
                data-testid="analytics-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </Button>
              <Button 
                onClick={handleCreateCourse}
                className="bg-primary hover:bg-primary/90"
                data-testid="create-course-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Course
              </Button>
            </div>
          </div>

          {/* Subscription Required Banner */}
          {!isSubscribed && (
            <Card className="mb-8 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Zap className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-slate-900">Subscribe to Start Teaching</h3>
                    <p className="text-slate-600">Choose a plan to list your courses and start earning</p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/instructor/pricing')}
                  className="bg-amber-500 hover:bg-amber-600"
                  data-testid="subscribe-instructor-cta"
                >
                  View Plans
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Stats Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {/* Courses */}
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-primary/10 text-primary">
                    <BookOpen className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats?.total_courses || 0}</p>
                    <p className="text-sm text-slate-600">Total Courses</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Students */}
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-100 text-blue-600">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats?.total_students || 0}</p>
                    <p className="text-sm text-slate-600">Total Students</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Earnings */}
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-green-100 text-green-600">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">${(earnings?.total_earnings || 0).toFixed(2)}</p>
                    <p className="text-sm text-slate-600">Total Earnings</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pending Payout */}
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-amber-100 text-amber-600">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">${(earnings?.pending_payout || 0).toFixed(2)}</p>
                    <p className="text-sm text-slate-600">Pending Payout</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Plan Status Card */}
          {isSubscribed && planStatus && (
            <Card className="mb-8 border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-heading font-semibold text-lg text-slate-900">
                          {planStatus.current_plan?.charAt(0).toUpperCase() + planStatus.current_plan?.slice(1)} Plan
                        </h3>
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                      </div>
                      <p className="text-slate-600">
                        {planStatus.courses_created} / {planStatus.course_limit === 999 ? '∞' : planStatus.course_limit} courses used
                      </p>
                    </div>
                  </div>
                  <div className="w-48">
                    <Progress 
                      value={(planStatus.courses_created / planStatus.course_limit) * 100} 
                      className="h-2"
                    />
                  </div>
                  <Button variant="outline" onClick={() => navigate('/instructor/pricing')}>
                    Upgrade Plan
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Courses List */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Your Courses</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : courses.length > 0 ? (
                <div className="divide-y divide-slate-200">
                  {courses.map((course) => (
                    <div 
                      key={course.id} 
                      className="py-4 flex items-center gap-4"
                      data-testid={`course-row-${course.id}`}
                    >
                      <img 
                        src={course.thumbnail_url} 
                        alt={course.title}
                        className="w-24 h-16 rounded-lg object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-slate-900 truncate">{course.title}</h3>
                          <Badge variant={course.is_published ? 'default' : 'secondary'} className="flex-shrink-0">
                            {course.is_published ? 'Published' : 'Draft'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-600">
                          <span className="font-semibold text-primary">${course.price}</span>
                          <span>{course.lessons?.length || 0} lessons</span>
                          <span>{course.students_count || 0} students</span>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`course-menu-${course.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/instructor/courses/${course.id}/edit`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Course
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => togglePublish(course.id, course.is_published)}>
                            {course.is_published ? (
                              <>
                                <EyeOff className="w-4 h-4 mr-2" />
                                Unpublish
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-2" />
                                Publish
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => deleteCourse(course.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">No courses yet</h3>
                  <p className="text-slate-600 mb-6">
                    {isSubscribed 
                      ? 'Create your first course to start teaching' 
                      : 'Subscribe to a plan to create courses'}
                  </p>
                  <Button 
                    onClick={isSubscribed ? handleCreateCourse : () => navigate('/instructor/pricing')}
                    data-testid="create-first-course-btn"
                  >
                    {isSubscribed ? (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Your First Course
                      </>
                    ) : (
                      'Subscribe to Start'
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Sales */}
          {earnings?.recent_sales?.length > 0 && (
            <Card className="mt-8 border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading">Recent Sales</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-slate-200">
                  {earnings.recent_sales.map((sale, index) => (
                    <div key={index} className="py-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">Course Sale</p>
                        <p className="text-sm text-slate-500">
                          {new Date(sale.purchased_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">+${sale.instructor_earnings?.toFixed(2)}</p>
                        <p className="text-xs text-slate-500">of ${sale.amount?.toFixed(2)} sale</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstructorDashboard;
